// Curated, CITED statistics library: the single source of truth for every number
// the Website Analyzer puts in front of a user.
//
// RULE: Only HIGH / MED-HIGH defensibility stats from primary research live here.
// The analyzer NEVER invents a number. Each recommendation references a stat by id;
// the UI always renders the source citation next to it.
//
// ───────────────────────────────────────────────────────────────────────────
// BANNED: do NOT add these. They are vendor lore with no defensible methodology
// and violate our no-unverified-marketing-claims rule:
//   • "Countdown timers boost conversions +332% / +400% / +300%"  (timer-app vendors)
//   • "Video on a landing page = +80% / +86% / +97% conversions"  (EyeView ~2010; Unbounce data contradicts)
//   • "Trust badges +15% / +75% / 1-3 signals = +23%"             (misattributed to Baymard; not in their work)
//   • "88% trust online reviews as much as personal recommendations" (outdated; now ~49%)
//   • "87% FAQ rich-result CTR"                                    (unsourced; FAQ rich results retired for commercial sites Aug 2023)
//   • Any single-decimal mobile-traffic-share or fixed self-service cost-per-ticket figure
// ───────────────────────────────────────────────────────────────────────────

export type Defensibility = 'high' | 'med-high';

export interface IBenchmarkStat {
  id: string;
  /** The headline number, shown large. e.g. "+270%", "53%", "19%". */
  number: string;
  /** Full, accurate claim sentence. */
  claim: string;
  sourceName: string;
  sourceUrl: string;
  year: number;
  defensibility: Defensibility;
}

export const BENCHMARKS: Record<string, IBenchmarkStat> = {
  reviews_powerreviews: {
    id: 'reviews_powerreviews',
    number: '+108.6%',
    claim:
      'Shoppers who interact with ratings and reviews on a product page convert at a 108.6% higher rate (analysis of 1.5M product pages across 1,200+ sites).',
    sourceName: 'PowerReviews, How UGC Impacts Conversion: 2023 Edition',
    sourceUrl: 'https://www.powerreviews.com/how-ugc-impacts-conversion-2023/',
    year: 2023,
    defensibility: 'high',
  },
  local_reviews_brightlocal: {
    id: 'local_reviews_brightlocal',
    number: '97%',
    claim:
      '97% of consumers read reviews for local businesses, and reviews directly influence which businesses they choose.',
    sourceName: 'BrightLocal Local Consumer Review Survey',
    sourceUrl: 'https://www.brightlocal.com/research/local-consumer-review-survey/',
    year: 2026,
    defensibility: 'med-high',
  },
  social_creator_bazaarvoice: {
    id: 'social_creator_bazaarvoice',
    number: '86%',
    claim:
      '86% of shoppers say they engage with creator and social content before making a buying decision (survey of 8,000+ global shoppers).',
    sourceName: 'Bazaarvoice Shopper Experience Index, Vol. 18',
    sourceUrl:
      'https://www.bazaarvoice.com/press/bazaarvoice-shopper-experience-index-vol-18-88-of-shoppers-want-an-omnichannel-experience-a-third-of-shoppers-say-that-includes-social/',
    year: 2024,
    defensibility: 'med-high',
  },
  faq_qa_74: {
    id: 'faq_qa_74',
    number: '74%',
    claim:
      '74% of online shoppers regularly read a Q&A or FAQ section before committing to a purchase.',
    sourceName: 'PowerReviews',
    sourceUrl:
      'https://www.prnewswire.com/news-releases/fueling-purchase-decisions-74-of-online-shoppers-regularly-read-qa-sections-before-committing-to-a-purchase-302130936.html',
    year: 2024,
    defensibility: 'med-high',
  },
  forms_baymard: {
    id: 'forms_baymard',
    number: '22%',
    claim:
      '22% of shoppers abandon checkout because the form or process was too long or complicated; the optimal form is around 7-8 fields.',
    sourceName: 'Baymard Institute',
    sourceUrl: 'https://baymard.com/lists/cart-abandonment-rate',
    year: 2024,
    defensibility: 'high',
  },
  popup_omnisend: {
    id: 'popup_omnisend',
    number: '~2-9%',
    claim:
      'Email capture popups convert at roughly 2% on average, with well-optimized popups reaching 9% or more (1.24 billion displays analyzed).',
    sourceName: 'Omnisend',
    sourceUrl: 'https://www.omnisend.com/blog/email-popup-statistics/',
    year: 2025,
    defensibility: 'med-high',
  },
  cart_baymard: {
    id: 'cart_baymard',
    number: '70%',
    claim:
      'The average documented online cart abandonment rate is 70.22%, much of it driven by friction, hesitation, and unclear pricing at the moment of purchase (average of 50 studies).',
    sourceName: 'Baymard Institute',
    sourceUrl: 'https://baymard.com/lists/cart-abandonment-rate',
    year: 2025,
    defensibility: 'high',
  },
  trust_card_19: {
    id: 'trust_card_19',
    number: '19%',
    claim:
      '19% of shoppers abandon checkout because they did not trust the site with their credit-card information.',
    sourceName: 'Baymard Institute',
    sourceUrl: 'https://baymard.com/blog/perceived-security-of-payment-form',
    year: 2025,
    defensibility: 'high',
  },
  mobile_share: {
    id: 'mobile_share',
    number: '~60%',
    claim:
      'Around 60% of all global web traffic now comes from mobile devices, and Google indexes mobile-first.',
    sourceName: 'Statista / StatCounter',
    sourceUrl:
      'https://www.statista.com/statistics/277125/share-of-website-traffic-coming-from-mobile-devices/',
    year: 2025,
    defensibility: 'high',
  },
  ai_overviews_pew: {
    id: 'ai_overviews_pew',
    number: '8% vs 15%',
    claim:
      'When a Google search shows an AI summary, users click through to a traditional result on just 8% of visits, versus 15% when no summary appears, roughly half as often (analysis of 68,879 searches from 900 U.S. adults, March 2025).',
    sourceName: 'Pew Research Center',
    sourceUrl:
      'https://www.pewresearch.org/short-reads/2025/07/22/google-users-are-less-likely-to-click-on-links-when-an-ai-summary-appears-in-the-results/',
    year: 2025,
    defensibility: 'high',
  },
};

export function getStat(id?: string): IBenchmarkStat | undefined {
  return id ? BENCHMARKS[id] : undefined;
}
